import { askGroq } from "./groq";
import { searchFlights, isTokenExpired } from "./amadeus";
import { supabase } from "@/integrations/supabase/client";
import { WEATHER_API_KEY, WEATHER_API_URL } from "@/config/api";
import { fetchNewsForCity } from './news';

// City code mappings for major Indian cities
const CITY_CODES: { [key: string]: string } = {
  "mumbai": "BOM",
  "delhi": "DEL",
  "bangalore": "BLR",
  "hyderabad": "HYD",
  "chennai": "MAA",
  "kolkata": "CCU",
  "pune": "PNQ",
  "ahmedabad": "AMD",
  "goa": "GOI",
  "jaipur": "JAI"
};

function getCityCode(cityName: string): string {
  const normalizedCity = cityName.toLowerCase().trim();
  return CITY_CODES[normalizedCity] || normalizedCity.toUpperCase().slice(0, 3);
}

// Function to parse any date format to YYYY-MM-DD using Groq
async function parseDateToStandardFormat(dateString: string): Promise<string> {
  try {
    const response = await askGroq([
      {
        role: "system",
        content: `You are a date parsing expert. Convert the given date to YYYY-MM-DD format.
        If the date is invalid or cannot be parsed, return "invalid".
        If the year is not specified, assume it's for the next occurrence of that date.
        Only respond with the date in YYYY-MM-DD format or "invalid", nothing else.`
      },
      {
        role: "user",
        content: `Convert this date to YYYY-MM-DD format: ${dateString}`
      }
    ]);

    const parsedDate = response.trim();
    
    // Validate the parsed date format
    if (parsedDate === "invalid" || !/^\d{4}-\d{2}-\d{2}$/.test(parsedDate)) {
      throw new Error('Invalid date format');
    }

    return parsedDate;
  } catch (error) {
    console.error('Error parsing date:', error);
    throw new Error('Unable to parse the provided date');
  }
}

interface TravelPlan {
  flights?: any[];
  hotels?: any[];
  weather?: any;
  news?: any[];
  destination: string;
  itinerary: string;
  budget: {
    transportation: number;
    accommodation: number;
    activities: number;
    total: number;
  };
}

// Cache for storing intermediate results
const resultCache = new Map<string, any>();

export async function planTrip(userInput: string, conversationHistory: Message[] = []): Promise<TravelPlan> {
  try {
    // Step 1: Extract travel parameters using Groq
    const cacheKey = `params_${userInput}`;
    let travelParams;
    
    if (resultCache.has(cacheKey)) {
      travelParams = resultCache.get(cacheKey);
    } else {
      // Filter out system messages and only include relevant conversation history
      const relevantHistory = conversationHistory.filter(msg => 
        msg.role === 'user' || msg.role === 'assistant'
      );

    const travelParamsResponse = await askGroq([
      {
        role: "system",
        content: `You are a travel parameter extractor. Your task is to extract travel details from user input and return ONLY a JSON object.
        
        Rules:
        1. ONLY return a valid JSON object, nothing else
        2. Do not include any explanations or text outside the JSON
        3. Use this exact format:
        {
          "destination": "city name",
          "origin": "city name or 'unknown'",
          "date": "exact date as mentioned or 'flexible'",
          "endDate": "end date if mentioned or 'flexible'",
          "preferences": {
            "budget": "budget|moderate|luxury",
            "interests": ["sightseeing", "food"]
          }
        }
        4. For interests, include common travel interests like sightseeing, food, culture, shopping, etc.
          5. If budget is not specified, default to "moderate"
          6. Consider the conversation history when extracting parameters
          7. If information is missing, use "unknown" or "flexible" as appropriate`
      },
        ...relevantHistory,
      { role: "user", content: userInput }
    ]);

    try {
      travelParams = JSON.parse(travelParamsResponse);
        resultCache.set(cacheKey, travelParams);
    } catch (e) {
      console.error('Failed to parse travel parameters:', e);
      console.error('Raw response:', travelParamsResponse);
      throw new Error('Failed to understand travel requirements. Please try rephrasing your request.');
      }
    }

    // Step 2: Parse and standardize the date format
    let standardizedStartDate = "flexible";
    if (travelParams.date && travelParams.date !== "flexible") {
      const dateCacheKey = `date_${travelParams.date}`;
      if (resultCache.has(dateCacheKey)) {
        standardizedStartDate = resultCache.get(dateCacheKey);
      } else {
      try {
        standardizedStartDate = await parseDateToStandardFormat(travelParams.date);
          resultCache.set(dateCacheKey, standardizedStartDate);
      } catch (error) {
        console.error('Date parsing error:', error);
        throw new Error('Unable to understand the provided date. Please provide a clear date.');
        }
      }
    }

    let standardizedEndDate = "flexible";
    if (travelParams.endDate && travelParams.endDate !== "flexible") {
      try {
        standardizedEndDate = await parseDateToStandardFormat(travelParams.endDate);
      } catch (error) {
        console.warn('End date parsing error:', error);
        // Don't throw error for end date, just keep it flexible
      }
    }

    // Step 3: Fetch flight, weather, and news data in parallel
    const [flightResults, weatherData, newsArticles] = await Promise.all([
      // Search flights using Amadeus API
      searchFlights(
        travelParams.origin !== "unknown" ? travelParams.origin : "Hyderabad",
        travelParams.destination,
        standardizedStartDate,
        standardizedEndDate !== "flexible" ? standardizedEndDate : undefined,
        1,
        'ECONOMY'
      ).catch((error) => {
        console.error('Flight search error:', error);
        return null;
      }),

      // Get weather forecast
      fetch(`${WEATHER_API_URL}?q=${travelParams.destination}&appid=${WEATHER_API_KEY}&units=metric`)
        .then(res => res.json())
        .catch(() => null),

      // Fetch news articles for the destination
      fetchNewsForCity(travelParams.destination)
        .catch(() => [])
    ]);

    // Calculate trip duration
    const tripDuration = standardizedEndDate !== "flexible" 
      ? getDaysDifference(standardizedStartDate, standardizedEndDate)
      : 3; // Default to 3 days if no end date specified

    // Calculate budget components
    const transportationCost = calculateTransportationCost(flightResults);
    const accommodationCost = calculateAccommodationCost([], tripDuration); // Empty array since hotels were removed
    const activitiesCost = estimateActivitiesCost(travelParams.preferences.budget || "moderate", tripDuration);
    
    // Calculate total budget
    const totalBudget = {
      transportation: transportationCost,
      accommodation: accommodationCost,
      activities: activitiesCost,
      total: transportationCost + accommodationCost + activitiesCost
    };

    // Generate comprehensive itinerary using Groq
    const contextPrompt = `
      Destination: ${travelParams.destination}
      Dates: ${standardizedStartDate} to ${standardizedEndDate}
      Weather: ${JSON.stringify(weatherData)}
      Transportation Options:
      - Flights: ${JSON.stringify(flightResults)}
      User Interests: ${travelParams.preferences.interests.join(", ")}
      Budget Category: ${travelParams.preferences.budget}
      Estimated Budget: ${JSON.stringify(totalBudget)}
    `;

    const itineraryCacheKey = `itinerary_${travelParams.destination}_${standardizedStartDate}`;
    let itinerary;
    
    if (resultCache.has(itineraryCacheKey)) {
      itinerary = resultCache.get(itineraryCacheKey);
    } else {
      itinerary = await askGroq([
      {
        role: "system",
        content: `You are an expert travel planner. Create a detailed itinerary based on the provided context.
        Include transportation details, daily activities, and estimated costs.
        Format the response in a clean, readable markdown format.`
      },
      { role: "user", content: contextPrompt }
    ]);
      resultCache.set(itineraryCacheKey, itinerary);
    }

    // Return the complete travel plan
    return {
      destination: travelParams.destination,
      origin: travelParams.origin,
      dates: {
        start: standardizedStartDate,
        end: standardizedEndDate
      },
      preferences: travelParams.preferences,
      flights: flightResults,
      weather: weatherData,
      news: newsArticles,
      itinerary,
      budget: totalBudget
    };

  } catch (error) {
    console.error('Error in planTrip:', error);
    throw error;
  }
}

// Helper functions
function calculateTransportationCost(flights: any): number {
  if (flights?.data?.[0]) {
    const flight = flights.data[0];
    // Use the actual price from Amadeus API
    return parseFloat(flight.price.total) || 5000;
  }
  return 5000; // Default cost if no flights found
}

function calculateAccommodationCost(hotels: any[], days: number): number {
  const averageNightlyRate = hotels.length > 0 
    ? hotels.reduce((acc, hotel) => {
        const priceRange = hotel.price_range || "3000-5000";
        const [min] = priceRange.split("-").map(Number);
        return acc + min;
      }, 0) / hotels.length
    : 3000; // Default nightly rate if no hotels found

  return averageNightlyRate * days;
}

function estimateActivitiesCost(budget: string, days: number): number {
  const dailyActivityCost = budget.toLowerCase().includes("luxury") ? 5000 :
    budget.toLowerCase().includes("premium") ? 3000 :
    budget.toLowerCase().includes("budget") ? 1000 : 2000;
  
  return dailyActivityCost * days;
}

function getDaysDifference(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 3; // Default to 3 days if calculation fails
} 